import { useMemo } from 'react';
import { useIntl } from 'react-intl';

import { getBrands } from '@/api-clients/get-brands';
import { getLocations } from '@/api-clients/get-locations';
import { getPositions } from '@/api-clients/get-positions';
import { useTableContext } from '@/contexts/table-context';
import { useFilterQuery } from '@/hooks/use-filter-query';

export function useOpeningsFilters() {
  const intl = useIntl();

  const {
    filters: {
      positionIds,
      locationIds,
      isSelectedForSourcing,
      brandIds,
      active,
    },
  } = useTableContext();

  const {
    data: positionsData,
    isFetching: isFetchingPositions,
    query: positionsQuery,
    setQuery: setPositionsQuery,
  } = useFilterQuery({
    key: 'positions-filter',
    fetchFn: getPositions,
  });

  const {
    data: locationsData,
    isFetching: isFetchingLocations,
    query: locationsQuery,
    setQuery: setLocationsQuery,
  } = useFilterQuery({
    key: 'locations-filter',
    fetchFn: getLocations,
  });

  const {
    data: brandsData,
    isFetching: isFetchingBrands,
    query: brandsQuery,
    setQuery: setBrandsQuery,
  } = useFilterQuery({
    key: 'brands-filter',
    fetchFn: getBrands,
  });

  const brandsOptions = brandsData?.brands?.map(({ id, name }) => ({
    label: name,
    value: id,
  }));

  const filters = useMemo(
    () => [
      {
        filterKey: 'isSelectedForSourcing',
        label: intl.formatMessage({
          defaultMessage: 'Dashboard status',
          description:
            'Table filter: whether the role appears in the org sourcing dashboard',
        }),
        options: [
          // This is defined as a string to be consistent with the combobox component. It will be converted to a boolean when sent to the backend.
          {
            label: intl.formatMessage({
              defaultMessage: 'All',
              description:
                'Filter option: do not filter by dashboard inclusion',
            }),
            value: undefined,
          },
          {
            label: intl.formatMessage({
              defaultMessage: 'Added',
              description:
                'Filter option: openings included on the sourcing dashboard',
            }),
            value: true,
          },
          {
            label: intl.formatMessage({
              defaultMessage: 'Not added',
              description:
                'Filter option: openings not on the sourcing dashboard',
            }),
            value: false,
          },
        ],
        isMultiSelect: false,
        selectedValues: [isSelectedForSourcing],
      },
      {
        filterKey: 'active',
        label: intl.formatMessage({
          defaultMessage: 'Status',
          description: 'Openings table filter: active vs inactive opening',
        }),
        options: [
          {
            label: intl.formatMessage({
              defaultMessage: 'All',
              description: 'Filter option: any active/inactive state',
            }),
            value: undefined,
          },
          {
            label: intl.formatMessage({
              defaultMessage: 'Active',
              description: 'Filter option: only active openings',
            }),
            value: true,
          },
          {
            label: intl.formatMessage({
              defaultMessage: 'Inactive',
              description: 'Filter option: only inactive openings',
            }),
            value: false,
          },
        ],
        isMultiSelect: false,
        selectedValues: [active],
      },
      {
        filterKey: 'brandIds',
        label: intl.formatMessage({
          defaultMessage: 'Brand',
          description: 'Openings table filter: employer brand name',
        }),
        isFetching: isFetchingBrands,
        query: brandsQuery,
        setQuery: setBrandsQuery,
        options: brandsOptions,
        isMultiSelect: true,
        selectedValues: brandIds,
      },
      {
        filterKey: 'positionIds',
        label: intl.formatMessage({
          defaultMessage: 'Position',
          description: 'Openings table filter: job position / role title',
        }),
        isFetching: isFetchingPositions,
        query: positionsQuery,
        setQuery: setPositionsQuery,
        options: positionsData?.positions?.map(({ id, title }) => ({
          label: title,
          value: id,
        })),
        isMultiSelect: true,
        selectedValues: positionIds,
      },
      {
        filterKey: 'locationIds',
        label: intl.formatMessage({
          defaultMessage: 'Location',
          description: 'Openings table filter: work site or location name',
        }),
        isFetching: isFetchingLocations,
        query: locationsQuery,
        setQuery: setLocationsQuery,
        options: locationsData?.locations?.map(({ id, name }) => ({
          label: name,
          value: id,
        })),
        isMultiSelect: true,
        selectedValues: locationIds,
      },
    ],
    [
      intl,
      isSelectedForSourcing,
      isFetchingPositions,
      positionsQuery,
      setPositionsQuery,
      positionsData?.positions,
      positionIds,
      isFetchingLocations,
      locationsQuery,
      setLocationsQuery,
      locationsData?.locations,
      locationIds,
      brandIds,
      brandsOptions,
      active,
      isFetchingBrands,
      brandsQuery,
      setBrandsQuery,
    ],
  );

  return filters;
}
